import React, { useEffect, useState } from 'react';
import { fetchUsers, getUserByEmail } from '../services/userService';
import { updateSubscriptionFields, removeUserFromSubscription } from '../services/subscriptionService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/PersonRemoveOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNavigate } from 'react-router-dom';
import { EngineeringOutlined as PersonnelIcon } from '@mui/icons-material';
import { useOrganization } from '../contexts/OrganizationContext';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

const Personnel = () => {
  const { selectedOrg, updateSelectedOrg } = useOrganization();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [existingEmail, setExistingEmail] = useState('');
  const navigate = useNavigate();

  const loadUsers = async () => {
    if (!selectedOrg) {
      setError('No organization selected.');
      setLoading(false);
      return;
    }

    try {
      const data = await fetchUsers(selectedOrg);
      setUsers(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch personnel data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [selectedOrg]);

  const handleAddUser = () => {
    setOpenDialog(true); // Open the dialog box
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setExistingEmail('');
  };

  const handleCreateNewUser = () => {
    setOpenDialog(false);
    navigate('/personnel/new'); // Navigate to the user creation form
  };

  const handleAddExistingUser = async () => {
    if (!existingEmail) {
      alert('Please enter a valid email address.');
      return;
    }

    try {
      const user = await getUserByEmail(existingEmail);

      if (user) {
        // Check if the new user is a pilot
        const isPilot = user.skills?.operational?.includes('remote_pic');

        if (isPilot) {
          // Fetch all current users in the subscription
          const allUsers = await fetchUsers(selectedOrg);

          // Check if there is already a pilot in the subscription
          const existingPilot = allUsers.some((existingUser) =>
            existingUser.skills?.operational?.includes('remote_pic')
          );

          if (existingPilot) {
            alert('This subscription is limited to one pilot. Please remove the other pilot before adding this user.');
            return; // Prevent adding the new pilot
          }
        }

        // Add the user to the subscription
        await updateSubscriptionFields(selectedOrg, { assignedUsers: [user.userGuid] }, 'add');
        //console.log('User successfully added to the subscription');
        await loadUsers(); // Reload users to update the UI
      } else {
        alert('User not found.');
      }
    } catch (err) {
      console.error('Error adding existing user:', err);
      alert('Failed to add user. Please try again.');
    } finally {
      handleDialogClose();
    }
  };

  const handleEditUser = (userGuid) => {
    navigate(`/personnel/${userGuid}`); // Navigate to user details page for editing
  };

  const handleDeleteUser = async (userGuid) => {
    // Confirm and delete user
    if (window.confirm('Are you sure you want to remove this user?')) {
      setUsers(users.filter((user) => user.userGuid !== userGuid));
      // Make API call to delete user
      try {
        await removeUserFromSubscription(selectedOrg, userGuid);
        alert('User removed from subscription successfully.');
        fetchUsers(selectedOrg); // Reload the list after removal
      } catch (error) {
        alert('Failed to remove user from subscription.');
      }
      console.log(`Deleted user: ${userGuid}`);
    }
  };

  {/*
  const handleOrgChange = (event) => {
    const orgId = event.target.value;
    updateSelectedOrg(orgId);
  };
  */}

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          // padding: '16px',
          backgroundColor: '#f9f9f9',
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            //marginBottom: 2,
            color: '#2c3e50',
            fontWeight: 'bold',
          }}
        >
          Loading Personnel...
        </Typography>
        <LinearProgress
          sx={{
            height: 4,
            borderRadius: 4,
            backgroundColor: '#e0e0e0', // Track color
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: '#3f51b5', // Bar color
            },
          }}
        />
      </Box>
    );
  }

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ padding: 0, backgroundColor: '#f9f9f9' }}>

      {/* Organization Dropdown */}
      {/*
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexGrow: 1,
          marginLeft: '16px',
          marginBottom: '24px',
        }}
      >
        {loading ? (
          <Typography variant="body2" sx={{ color: '#ecf0f1' }}>
            Loading organizations...
          </Typography>
        ) : error ? (
          <Typography variant="body2" sx={{ color: 'red' }}>
            {error}
          </Typography>
        ) : (
          <FormControl
            sx={{
              minWidth: 200,
              marginRight: 2,
              backgroundColor: '#2c3e50', // Background color
              borderRadius: '8px', // Rounded corners
              height: '36px', // Reduced height
              '& .MuiInputBase-root': {
                height: '36px', // Ensure dropdown matches the height
              },
            }}
          >
            <Select
              value={selectedOrg}
              onChange={handleOrgChange}
              sx={{
                color: '#ecf0f1', // Text color
                backgroundColor: '#2c3e50', // Dropdown background color
                borderRadius: '8px', // Rounded corners for the dropdown
                height: '100%', // Ensure height matches the container
                '& .MuiSelect-icon': { color: '#ecf0f1' }, // Icon color
              }}
            >
              {subscriptions.map((sub) => (
                <MenuItem
                  key={sub.subscriptionGuid}
                  value={sub.subscriptionGuid}
                  sx={{
                    color: '#2c3e50', // Darker text color for items
                    '&.Mui-selected': {
                      backgroundColor: '#e0f7fa', // Subtle background for selected item
                      color: '#2c3e50', // Maintain dark text for selected
                    },
                    '&:hover': {
                      backgroundColor: '#f5f5f5', // Hover effect for items
                    },
                  }}
                >
                  {sub.orgName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      */}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 6,
          paddingLeft: '16px',
          paddingRight: '16px',
        }}
      >
        {/* Left-aligned content */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center', // Vertically center icon and text
            gap: 1, // Add spacing between the icon and text
          }}
        >
          <PersonnelIcon sx={{ fontSize: '24px', color: '#2c3e50' }} />
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: '#2c3e50' }}
          >
            Personnel
          </Typography>
        </Box>

        {/* Right-aligned button */}
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleAddUser}
        >
          Add Personnel
        </Button>
      </Box>

      {/* Dialog for Add Personnel */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Add Personnel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Would you like to create a new user or add an existing user by email?
          </DialogContentText>
          <Box sx={{ marginTop: 2 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleCreateNewUser}
              sx={{ marginBottom: 2 }}
            >
              Create New User
            </Button>
            <TextField
              label="Enter Existing User Email"
              fullWidth
              value={existingEmail}
              onChange={(e) => setExistingEmail(e.target.value)}
              sx={{ marginBottom: 2 }}
            />
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleAddExistingUser}
            >
              Add Existing User
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ padding: '4px 16px', fontWeight: 'bold', color: '#2c3e50' }}>
              Name
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Email
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Phone Number
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Subscription Plan
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Created At
            </TableCell>
            <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.userGuid}>
              <TableCell sx={{ padding: '4px 16px' }}>{`${user.firstName} ${user.lastName}`}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>{user.email}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>{user.phoneNumber || 'N/A'}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>
                {user.subsPlan === 0
                  ? 'Free'
                  : user.subsPlan === 1
                    ? 'Pilot Pro'
                    : 'Enterprise'}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>
                {new Date(user.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>
                <Tooltip title="Edit">
                  <IconButton
                    color="primary"
                    onClick={() => handleEditUser(user.userGuid)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteUser(user.userGuid)}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default Personnel;